/**
 * useDQCategoryDrawer Hook
 * ============================================================================
 * Custom React hook for managing the Data Quality Category Drawer state
 * and fetching category details from the API.
 *
 * Usage:
 * ```tsx
 * const { isOpen, categoryDetail, loading, error, openDrawer, closeDrawer } =
 *   useDQCategoryDrawer(jobId);
 *
 * // Later, listen to events:
 * useEffect(() => {
 *   const handler = (e) => openDrawer(e.detail.categoryId);
 *   window.addEventListener('dq-category-click', handler);
 *   return () => window.removeEventListener('dq-category-click', handler);
 * }, [openDrawer]);
 * ```
 *
 * Related Files:
 * - client/types/dataQualityCategory.ts (type definitions)
 * - client/components/dataQuality/DataQualityCategoryDrawer.tsx (UI component)
 *
 * @version 1.0.0
 * @created 2026-01-28
 * ============================================================================
 */

import { useState, useCallback } from 'react';
import type {
  DataQualityCategoryId,
  DataQualityCategoryDetail,
  GetCategoryDetailResponse,
} from '../../types/dataQualityCategory';
import { isValidCategoryId } from '../../types/dataQualityCategory';

// ============================================================================
// Types
// ============================================================================

export interface UseDQCategoryDrawerReturn {
  /** Whether the drawer is currently open */
  isOpen: boolean;
  /** The category detail data (null if not loaded) */
  categoryDetail: DataQualityCategoryDetail | null;
  /** Loading state */
  loading: boolean;
  /** Error message (null if no error) */
  error: string | null;
  /** The currently selected category ID */
  selectedCategoryId: DataQualityCategoryId | null;
  /** Open the drawer and fetch details for a category */
  openDrawer: (categoryId: DataQualityCategoryId) => Promise<void>;
  /** Close the drawer */
  closeDrawer: () => void;
  /** Reset error state */
  clearError: () => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for managing Data Quality Category Drawer state and API calls
 *
 * @param jobId - The Data Quality job ID to fetch category details from
 * @returns Drawer state and control functions
 */
export function useDQCategoryDrawer(jobId: string | null): UseDQCategoryDrawerReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [categoryDetail, setCategoryDetail] = useState<DataQualityCategoryDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<DataQualityCategoryId | null>(null);

  /**
   * Fetch category details from the API
   */
  const fetchCategoryDetail = useCallback(
    async (categoryId: DataQualityCategoryId): Promise<DataQualityCategoryDetail | null> => {
      if (!jobId) {
        throw new Error('No job ID provided');
      }

      const response = await fetch(`/api/data-quality/${jobId}/categories/${categoryId}`);
      const data: GetCategoryDetailResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to fetch category details: ${response.statusText}`);
      }

      return data.data || null;
    },
    [jobId]
  );

  /**
   * Open the drawer and fetch details for a category
   */
  const openDrawer = useCallback(
    async (categoryId: DataQualityCategoryId) => {
      // Validate category ID
      if (!isValidCategoryId(categoryId)) {
        setError(`Invalid category ID: ${categoryId}`);
        return;
      }

      // Check for job ID
      if (!jobId) {
        setError('No Data Quality job ID available');
        return;
      }

      // Set state for loading
      setSelectedCategoryId(categoryId);
      setIsOpen(true);
      setLoading(true);
      setError(null);
      setCategoryDetail(null);

      try {
        const detail = await fetchCategoryDetail(categoryId);
        setCategoryDetail(detail);
      } catch (err: unknown) {
        console.error(`[useDQCategoryDrawer] Failed to fetch ${categoryId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load category details');
      } finally {
        setLoading(false);
      }
    },
    [jobId, fetchCategoryDetail]
  );

  /**
   * Close the drawer
   */
  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    // Don't clear category detail immediately to allow for close animation
    setTimeout(() => {
      setCategoryDetail(null);
      setSelectedCategoryId(null);
      setError(null);
    }, 300);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isOpen,
    categoryDetail,
    loading,
    error,
    selectedCategoryId,
    openDrawer,
    closeDrawer,
    clearError,
  };
}

// ============================================================================
// Event Helper
// ============================================================================

/**
 * Dispatch a DQ category click event
 * Used by vanilla JS code to trigger the React drawer
 *
 * @param categoryId - The category ID that was clicked
 * @param score - The current score for the category
 */
export function dispatchDQCategoryClick(
  categoryId: DataQualityCategoryId,
  score: number
): void {
  const event = new CustomEvent('dq-category-click', {
    detail: { categoryId, score },
    bubbles: true,
    cancelable: true,
  });
  window.dispatchEvent(event);
}

export default useDQCategoryDrawer;
